import React, { Fragment } from 'react';
import { useParentDetails } from '../hooks/parents.hook';
import PaymentManager_v2 from '../components/molecules/PaymentManager_v2';
import PageHeader from '../components/molecules/PageHeader';
import StatusBar from '../components/molecules/StatusBar';
import FormSubmissionButtons from '../components/molecules/FormSubmissionButtons';
import SectionBox from '../components/atoms/SectionBox';
import { Link } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import {
  TextField,
  MenuItem,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button as MuiButton,
} from '@mui/material';

import { useNavigate } from 'react-router-dom';
import { getAdminInfo } from '../helper';

const EditParentDetails = () => {
  const {
    data,
    refetch,
    buttonLoadingStatus,
    handleFormUpdate,
    parentDetails,
    formSubmission,
    snackOpen,
    snackClose,
    snackMsg,
    snackBarType,
    hasChildren,
    deleteDialogOpen,
    openDeleteDialog,
    closeDeleteDialog,
    submitDeleteParent,
    deleteLoading,
  } = useParentDetails();

  const navigate = useNavigate();
  const isAdmin = Number(getAdminInfo()?.admin_level) === 9;

  return (
    <div>
      <PageHeader
        backLink={
          <Link onClick={() => navigate(-1)}>
            <ArrowBackIosNewIcon /> Back to previous page
          </Link>
        }
      >
        Parent: {data?.parent?.parent_name} <EditIcon />
      </PageHeader>

      <SectionBox title='Children' testid='children-info-box'>
        <div
          className='grid-cols-3 items-center border p-2 sm:grid sm:gap-6'
          id='children-list'
        >
          {data?.children?.map((child, index) => {
            return (
              <React.Fragment key={index}>
                <div className='py-2'>
                  <Link
                    to={`/manage/students/${child.student_id}`}
                    reloadDocument
                  >
                    {child.student_first_name} {child.student_last_name}
                  </Link>
                </div>
                <div className='col-span-2 mb-4 text-center sm:mb-0 sm:text-left'>
                  <PaymentManager_v2
                    student_id={child.student_id}
                    paymentMade={child.payment}
                    student_name={child.student_bc_name}
                    refetch={refetch}
                  />
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </SectionBox>

      <SectionBox title='Parent Information' testid='parent-edit-info-box'>
        <div className='grid grid-cols-6 gap-4 md:w-3/4'>
          <div className='col-span-6 md:col-span-4'>
            <TextField
              label='Parent Name'
              fullWidth
              name='parent_name'
              value={parentDetails?.parent_name ?? ''}
              onChange={handleFormUpdate}
              inputProps={{ 'data-cy': 'parent-name-field' }}
            />
          </div>

          <div className='col-span-6 md:col-span-3 md:col-start-1'>
            <TextField
              label='Relationship'
              fullWidth
              name='parent_relationship'
              value={parentDetails?.parent_relationship ?? ''}
              onChange={handleFormUpdate}
              inputProps={{ 'data-cy': 'parent-relationship-field' }}
            />
          </div>

          <div className='col-span-6 md:col-span-3 md:col-start-1'>
            <TextField
              label='ID'
              fullWidth
              name='parent_identity_number'
              value={parentDetails?.parent_identity_number ?? ''}
              onChange={handleFormUpdate}
              inputProps={{ 'data-cy': 'parent-ID-field' }}
            />
          </div>

          <div className='col-span-6 md:col-span-3 md:col-start-1'>
            <TextField
              label='Mobile'
              fullWidth
              name='parent_mobile'
              value={parentDetails?.parent_mobile ?? ''}
              onChange={handleFormUpdate}
              inputProps={{ 'data-cy': 'parent-mobile-field' }}
            />
          </div>

          <div className='col-span-6 md:col-span-3 md:col-start-1'>
            <TextField
              label='Email'
              fullWidth
              name='parent_email'
              value={parentDetails?.parent_email ?? ''}
              onChange={handleFormUpdate}
              inputProps={{ 'data-cy': 'parent-email-field' }}
            />
          </div>

          <div className='col-span-6 md:col-span-3 md:col-start-1'>
            <TextField
              label='Religion'
              fullWidth
              name='parent_religion'
              value={parentDetails?.parent_religion ?? ''}
              onChange={handleFormUpdate}
              inputProps={{ 'data-cy': 'parent-religion-field' }}
              select
              id='parent_religion'
            >
              <MenuItem value='catholic'>Catholic</MenuItem>
              <MenuItem value='non-catholic'>Non Catholic</MenuItem>
            </TextField>
          </div>
        </div>
      </SectionBox>

      <div className='my-4 flex items-center justify-end'>
        <FormSubmissionButtons
          submissionHandler={formSubmission}
          loadStatus={buttonLoadingStatus}
        />
        {isAdmin && (
          <button
            className='ml-4 rounded-md border bg-red-600 p-4 font-bold text-white shadow-md disabled:cursor-not-allowed disabled:opacity-50'
            onClick={openDeleteDialog}
            disabled={hasChildren}
            data-cy='delete-parent-button'
          >
            Delete Parent
          </button>
        )}
      </div>

      {isAdmin && (
        <>
          <Dialog
            open={deleteDialogOpen}
            onClose={closeDeleteDialog}
            maxWidth='sm'
            fullWidth
          >
            <DialogTitle>Delete Parent</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete{' '}
                <strong>{data?.parent?.parent_name}</strong>? This action is{' '}
                <strong>permanent and cannot be undone</strong>.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <MuiButton
                onClick={closeDeleteDialog}
                data-cy='cancel-delete-parent'
              >
                Cancel
              </MuiButton>
              <MuiButton
                onClick={submitDeleteParent}
                color='error'
                variant='contained'
                disabled={deleteLoading}
                data-cy='confirm-delete-parent'
              >
                Delete
              </MuiButton>
            </DialogActions>
          </Dialog>
        </>
      )}

      <Snackbar
        open={snackOpen}
        autoHideDuration={6000}
        onClose={snackClose}
        id='snackbar'
      >
        <StatusBar type={snackBarType} closeHandler={snackClose}>
          {snackMsg}
        </StatusBar>
      </Snackbar>
    </div>
  );
};

export default EditParentDetails;
